import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTagsStore } from '@/stores/tagsStore';
import { useHistoryStore, createHistoryEntry } from '@/stores/historyStore';
import { executeTagExtraction } from '@/services/tagsApi';
import { TextArea } from '@/components/TextArea';
import { Button } from '@/components/Button';
import { Select } from '@/components/Select';
import { ChipEditor } from '@/components/ChipEditor/ChipEditor';
import { ResultPanel } from '@/components/ResultPanel/ResultPanel';
import { DebugPanel } from '@/components/DebugPanel/DebugPanel';
import { showToast } from '@/components/Toast/Toast';
import { readMarkdownFile, isValidMarkdownFile, readValuesFile, isValidValuesFile } from '@/utils/parseValuesFile';
import { SUPPORTED_LANGUAGES, LANGUAGE_LABELS } from '@/constants/languages';
import type { FileUploadMode, TagExtractionResponse, LanguageCode } from '@/types/api';
import styles from './GrammaticalReferencePage.module.css';

export function GrammaticalReferencePage() {
  const { t } = useTranslation();
  const markdownInputRef = useRef<HTMLInputElement>(null);
  const valuesInputRef = useRef<HTMLInputElement>(null);
  const [uploadMode, setUploadMode] = useState<FileUploadMode>('replace');

  const {
    grammatical_reference,
    setGrammaticalReferenceLessonMarkdown,
    setGrammaticalReferenceLanguage,
    setGrammaticalReferenceValidValues,
    setGrammaticalReferenceLoading,
    setGrammaticalReferenceError,
    setGrammaticalReferenceResponse,
    loadGrammaticalReferenceDefaults,
    applyGrammaticalReferenceValuesFromFile,
    clearGrammaticalReference,
  } = useTagsStore();

  const { addEntry } = useHistoryStore();

  const languageOptions = SUPPORTED_LANGUAGES.map((code) => ({
    value: code,
    label: LANGUAGE_LABELS[code],
  }));

  const handleSubmit = useCallback(async () => {
    if (!grammatical_reference.lesson_markdown.trim()) {
      showToast('error', t('errors.lessonRequired'));
      return;
    }
    if (grammatical_reference.valid_values.length === 0) {
      showToast('error', t('errors.validValuesRequired'));
      return;
    }

    setGrammaticalReferenceLoading(true);

    const result = await executeTagExtraction(
      'grammatical_reference',
      grammatical_reference.lesson_markdown,
      grammatical_reference.valid_values,
      grammatical_reference.language
    );

    addEntry(
      createHistoryEntry(
        'grammatical_reference',
        result.requestPayload,
        result.ok ? (result.data as TagExtractionResponse) : null,
        result.responseHeaders || {},
        result.error || null
      )
    );

    if (result.ok && result.data) {
      setGrammaticalReferenceResponse(result.data as TagExtractionResponse, result.traceId || null);
      showToast('success', t('toast.submitSuccess'));
    } else {
      setGrammaticalReferenceError(result.error || t('errors.unknownError'));
    }
  }, [grammatical_reference, t, setGrammaticalReferenceLoading, setGrammaticalReferenceResponse, setGrammaticalReferenceError, addEntry]);

  const handleMarkdownUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      if (!isValidMarkdownFile(file)) { showToast('error', t('errors.invalidFileFormat')); return; }
      try {
        const content = await readMarkdownFile(file);
        setGrammaticalReferenceLessonMarkdown(content);
        showToast('success', t('toast.loadedFromFile'));
      } catch { showToast('error', t('errors.fileReadError')); }
      if (markdownInputRef.current) markdownInputRef.current.value = '';
    },
    [t, setGrammaticalReferenceLessonMarkdown]
  );

  const handleValuesUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      if (!isValidValuesFile(file)) { showToast('error', t('errors.invalidValuesFileFormat')); return; }
      try {
        const values = await readValuesFile(file);
        applyGrammaticalReferenceValuesFromFile(uploadMode, values);
        showToast('success', t('toast.valuesLoaded', { count: values.length }));
      } catch { showToast('error', t('errors.fileReadError')); }
      if (valuesInputRef.current) valuesInputRef.current.value = '';
    },
    [t, uploadMode, applyGrammaticalReferenceValuesFromFile]
  );

  const isSubmitDisabled = !grammatical_reference.lesson_markdown.trim() || grammatical_reference.valid_values.length === 0 || grammatical_reference.loading;

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.title}>{t('pages.grammaticalReference.title')}</h1>
        <p className={styles.description}>{t('pages.grammaticalReference.description')}</p>
      </header>

      <form className={styles.form} onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
        <div className={styles.field}>
          <TextArea
            label={t('form.lessonMarkdown')}
            value={grammatical_reference.lesson_markdown}
            onChange={(e) => setGrammaticalReferenceLessonMarkdown(e.target.value)}
            placeholder={t('form.lessonMarkdownPlaceholder')}
            rows={10}
            disabled={grammatical_reference.loading}
          />
          <div className={styles.fileUpload}>
            <input ref={markdownInputRef} type="file" accept=".md,.markdown" onChange={handleMarkdownUpload} className={styles.fileInput} id="markdown-upload-gr" />
            <label htmlFor="markdown-upload-gr" className={styles.fileLabel}>{t('form.uploadMarkdown')}</label>
          </div>
        </div>

        <div className={styles.field}>
          <Select
            label={t('form.language')}
            options={languageOptions}
            value={grammatical_reference.language}
            onChange={(e) => setGrammaticalReferenceLanguage(e.target.value as LanguageCode)}
            disabled={grammatical_reference.loading}
          />
        </div>

        <div className={styles.field}>
          <ChipEditor
            label={t('form.validValues')}
            values={grammatical_reference.valid_values}
            onChange={setGrammaticalReferenceValidValues}
            disabled={grammatical_reference.loading}
            error={grammatical_reference.valid_values.length === 0 ? t('errors.validValuesRequired') : undefined}
          />
          <div className={styles.valuesActions}>
            <Button type="button" variant="secondary" size="small" onClick={loadGrammaticalReferenceDefaults} disabled={grammatical_reference.loading}>
              {t('form.loadDefaults')}
            </Button>
            <div className={styles.uploadControls}>
              <select value={uploadMode} onChange={(e) => setUploadMode(e.target.value as FileUploadMode)} className={styles.modeSelect}>
                <option value="replace">{t('form.replace')}</option>
                <option value="append">{t('form.append')}</option>
              </select>
              <input ref={valuesInputRef} type="file" accept=".txt" onChange={handleValuesUpload} className={styles.fileInput} id="values-upload-gr" />
              <label htmlFor="values-upload-gr" className={styles.fileLabel}>{t('form.uploadValues')}</label>
            </div>
          </div>
        </div>

        <div className={styles.actions}>
          <Button type="submit" disabled={isSubmitDisabled} loading={grammatical_reference.loading}>{t('common.submit')}</Button>
          <Button type="button" variant="secondary" onClick={clearGrammaticalReference} disabled={grammatical_reference.loading}>{t('common.clear')}</Button>
        </div>
      </form>

      <ResultPanel response={grammatical_reference.lastResponse} traceId={grammatical_reference.lastTraceId} error={grammatical_reference.error} loading={grammatical_reference.loading} />
      <DebugPanel
        requestPayload={grammatical_reference.lastResponse ? { lesson_markdown: grammatical_reference.lesson_markdown, language: grammatical_reference.language, valid_values: grammatical_reference.valid_values } : null}
        responsePayload={grammatical_reference.lastResponse}
        responseHeaders={grammatical_reference.lastTraceId ? { 'x-request-id': grammatical_reference.lastTraceId } : {}}
      />
    </div>
  );
}
